import type {
  DataGridCellRendererProps,
  DataGridColumnSettings,
  DataGridSettings,
  DialogRef,
  DynamicFieldComponentProps,
  DynamicFormButtonSetting,
  JsonData,
} from '@myosh/odin-components';
import { DataGrid } from '@myosh/odin-components';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Observable } from 'rxjs';
import type { RecordResult } from '../../../@types/records';
import { useDeleteRecordCollaboratorMutation, useGetRecordCollaboratorsQuery } from '../../../redux/services/record';
import { promiseToast } from '../../../services/notification.service';
import { forceAssert } from '../../../utils/type-guards';
import ConfirmationDialog from '../../common/confirmation-dialog.component';
import InviteButtonField from '../invite-button-field/invite-button-field.component';
import InviteDialog from '../invite-button-field/invite-modal.component';
import InvitationActionsCell from './invitation-action-cell';

export interface InvitationFieldProps extends DynamicFieldComponentProps<JsonData> {
  recordId?: number;
  permissions?: { read: boolean; edit: boolean };
  saveRecord?: () => Observable<RecordResult>;
  updateLastChangeValue?: (lastChange: string) => void;
  setFormButtonsState?: React.Dispatch<React.SetStateAction<DynamicFormButtonSetting[]>>;
}

export interface Invitation {
  id: number;
  email: string;
  status: string;
  collaboratorEmailId?: number;
}
export interface CollaboratorsRequestBody {
  recordId: number;
}

function InvitationField({
  recordId,
  permissions,
  readOnly,
  saveRecord,
  updateLastChangeValue,
  setFormButtonsState,
}: InvitationFieldProps) {
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const invitationEmail = useRef<Invitation | undefined>(undefined);
  const continueModalReference = useRef<DialogRef>(null);
  const removeCollaboratorId = useRef<number | undefined>(undefined);
  const { t } = useTranslation();

  const [deleteCollaborator, { isLoading }] = useDeleteRecordCollaboratorMutation();

  const onDeleteCollaboratorClick = () => {
    if (!removeCollaboratorId.current) return;

    promiseToast(deleteCollaborator(removeCollaboratorId.current).unwrap(), {
      success: t('remove-collaborator'),
      error: t('error-removing-collaborator'),
      loading: t('removing-collaborator'),
    });
  };

  const { data: recordInvitations } = useGetRecordCollaboratorsQuery(
    { recordId: recordId ?? 0 },
    { skip: recordId === undefined || isNaN(recordId) || permissions?.read === false }
  );

  const showResendInvitationModal = (invitation: Invitation) => {
    invitationEmail.current = invitation;
    setShowInviteDialog(true);
  };

  const showDeleteCollaboratorModal = (collaboratorId: number) => {
    removeCollaboratorId.current = collaboratorId;
    continueModalReference.current?.show();
  };

  const onRemoveConfirmationModalClose = () => {
    removeCollaboratorId.current = undefined;
  };

  const onCloseDialog = () => setShowInviteDialog(false);

  const invitationGridColumns: Array<DataGridColumnSettings> = [
    {
      id: 1,
      field: 'email',
      title: t('email'),
      visible: true,
      width: 210,
    },
    {
      id: 2,
      field: 'status',
      title: t('status'),
      visible: true,
      width: 70,
    },
    {
      id: 3,
      field: '',
      title: '',
      visible: !readOnly,
      cellRenderer: 'INVITATION_ACTIONS',
      type: 6,
      disableSort: true,
    },
    {
      id: 0,
      field: 'id',
      title: t('id'),
      visible: false,
      isIdField: true,
    },
  ];

  const gridSettings: DataGridSettings = {
    columns: invitationGridColumns,
    fullHeight: true,
    rowIsClickable: false,
    components: {
      INVITATION_ACTIONS: {
        CellComponent: (props: DataGridCellRendererProps) => {
          return (
            <InvitationActionsCell
              showResendInvitationModal={showResendInvitationModal}
              showDeleteCollaboratorModal={showDeleteCollaboratorModal}
              invitation={{ ...props.rowData } as unknown as Invitation}
            />
          );
        },
      },
    },
  };

  useEffect(() => {
    if (!readOnly) {
      setFormButtonsState?.((prev) =>
        prev.map((button) => ({
          ...button,
          disabled: isLoading,
        }))
      );
    }
  }, [isLoading, readOnly, setFormButtonsState]);

  return (
    <>
      {permissions?.edit && (
        <InviteButtonField
          buttonCaption={t('invite')}
          recordId={recordId}
          readOnly={readOnly}
          saveRecord={saveRecord}
          updateLastChangeValue={updateLastChangeValue}
          setFormButtonsState={setFormButtonsState}
        />
      )}
      {permissions?.read && recordInvitations && recordInvitations?.length > 0 && (
        <>
          <div className="mt-2">
            <DataGrid
              data={forceAssert<JsonData>(recordInvitations) ?? []}
              gridSettings={gridSettings}
              showSettings={false}
            />
          </div>
          <InviteDialog
            invitationEmail={invitationEmail.current}
            recordId={recordId ?? 0}
            visible={showInviteDialog}
            hide={onCloseDialog}
            updateLastChangeValue={updateLastChangeValue}
            setFormButtonsState={setFormButtonsState}
            readOnly={readOnly}
          />
          <ConfirmationDialog
            ref={continueModalReference}
            header={t('contractor-invite-will-be-removed')}
            confirmButtonText={t('remove')}
            cancelButtonText={t('do-not-remove')}
            onConfirm={onDeleteCollaboratorClick}
            onClose={onRemoveConfirmationModalClose}
            hasCancelButton
          >
            <p>{t('continue-message')}</p>
          </ConfirmationDialog>
        </>
      )}
    </>
  );
}

export default React.memo(InvitationField);
